import {
  AppCard,
  PageContainer,
  RouteTabLink,
} from "@equinor/e-procurement-portal-and-ts-portal-front-end-components";
import { TabContext } from "@mui/lab";
import { Box, Pagination, Tabs } from "@mui/material";
import { createFileRoute } from "@tanstack/react-router";
import { enqueueSnackbar } from "notistack";
import { useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { useListServiceQuotations } from "../../../../__generated__/orval/endpoints/service-quotations/service-quotations";
import type { ListServiceQuotationsParams } from "../../../../__generated__/orval/models/listServiceQuotationsParams.zod";
import { ListServiceQuotations } from "../../../../components/molecules/ListServiceQuotations";
import { ForbiddenError } from "../../../../errors/ForbiddenError";
import { ServiceQuotationsFilters } from "./-internal_components/ServiceQuotationsFilters/index";
import { serviceQuotationsListQueryOptions } from "./-queries";

type ListServiceQuotationsParamsWithSingleDate = ListServiceQuotationsParams & {
  "order[assigned_to]"?: "asc" | "desc";
  created_at?: string;
  "order[plant]"?: "asc" | "desc";
  "order[pr_item.requisition_date]"?: "asc" | "desc";
  "order[proposals_count]"?: "asc" | "desc";
  "order[purchase_group]"?: "asc" | "desc";
  "order[renegotiation]"?: "asc" | "desc";
  "order[requester]"?: "asc" | "desc";
  "order[short_description]"?: "asc" | "desc";
  "order[status]"?: "asc" | "desc";
};

const searchSchema = z.object({
  assigned_to: z
    .optional(
      z.union([
        z.array(z.union([z.string(), z.number()])),
        z.string(),
        z.number(),
      ]),
    )
    .catch(undefined),
  created_at: z.optional(z.string()).catch(undefined),
  identifier: z.optional(z.union([z.string(), z.number()])).catch(undefined),
  order: z
    .optional(
      z.object({
        assigned_to: z.optional(z.enum(["asc", "desc"])).catch(undefined),
        identifier: z.optional(z.enum(["asc", "desc"])).catch(undefined),
        plant: z.optional(z.enum(["asc", "desc"])).catch(undefined),
        "pr_item.requisition_date": z
          .optional(z.enum(["asc", "desc"]))
          .catch(undefined),
        proposals_count: z.optional(z.enum(["asc", "desc"])).catch(undefined),
        purchase_group: z.optional(z.enum(["asc", "desc"])).catch(undefined),
        renegotiation: z.optional(z.enum(["asc", "desc"])).catch(undefined),
        requester: z.optional(z.enum(["asc", "desc"])).catch(undefined),
        short_description: z.optional(z.enum(["asc", "desc"])).catch(undefined),
        status: z.optional(z.enum(["asc", "desc"])).catch(undefined),
      }),
    )
    .default({ identifier: "desc" })
    .catch({ identifier: "desc" }),
  page: z.optional(z.coerce.number()).catch(undefined),
  plant_ids: z
    .optional(
      z.union([
        z.array(z.union([z.string(), z.number()])),
        z.string(),
        z.number(),
      ]),
    )
    .catch(undefined),
  pr_number: z.optional(z.string()).catch(undefined),
  proposals_count: z.optional(z.coerce.number()).catch(undefined),
  purchase_group_ids: z
    .optional(
      z.union([
        z.array(z.union([z.string(), z.number()])),
        z.string(),
        z.number(),
      ]),
    )
    .catch(undefined),
  reference_number: z.optional(z.string()).catch(undefined),
  renegotiation: z.optional(z.boolean()).catch(undefined),
  requester: z
    .optional(
      z.union([
        z.array(z.union([z.string(), z.number()])),
        z.string(),
        z.number(),
      ]),
    )
    .catch(undefined),
  rowsPerPage: z.optional(z.coerce.number()).catch(undefined),
  short_description: z.optional(z.string()).catch(undefined),
  status_name: z
    .optional(z.union([z.array(z.string()), z.string()]))
    .catch(undefined),
  tab: z
    .enum(["all", "open", "ongoing", "pending_buyer", "completed", "finished"])
    .default("all")
    .catch("all"),
});

type OrderSearchSchema = z.infer<typeof searchSchema.shape.order>;
type TabType = z.infer<typeof searchSchema.shape.tab>;
type ServiceQuotationsSearch = z.infer<typeof searchSchema>;

const parseSearchIds = (
  value?: string | number | Array<string | number>,
): number[] | undefined => {
  if (value === undefined) return undefined;

  const normalize = (rawValue: string | number): Array<string | number> => {
    if (typeof rawValue === "number") return [rawValue];

    const trimmedValue = rawValue.trim();

    if (trimmedValue.startsWith("[") && trimmedValue.endsWith("]")) {
      try {
        const parsedJson = JSON.parse(trimmedValue);
        if (Array.isArray(parsedJson)) return parsedJson;
      } catch {
        // Fallback to split below.
      }
    }

    if (trimmedValue.includes(",")) {
      return trimmedValue.split(",").map((item) => item.trim());
    }

    return [trimmedValue];
  };

  const normalized = Array.isArray(value)
    ? value.flatMap((item) => normalize(item))
    : normalize(value);

  const parsed = normalized
    .map((item) => Number(item))
    .filter((id) => Number.isInteger(id));

  return parsed.length > 0 ? parsed : undefined;
};

const parseSearchStrings = (
  value?: string | string[],
): string[] | undefined => {
  if (value === undefined) return undefined;

  const parsed = (Array.isArray(value) ? value : value.split(","))
    .map((item) => item.trim())
    .filter((item) => item.length > 0);

  return parsed.length > 0 ? parsed : undefined;
};

const ServiceQuotations: React.FC = () => {
  const { t } = useTranslation();

  const navigate = Route.useNavigate();
  const { order = {}, page = 1, rowsPerPage = 25, tab } = Route.useSearch();
  const loaderDeps = Route.useLoaderDeps();
  const selectedTab = tab === "finished" ? "completed" : tab;

  const listParams: ListServiceQuotationsParamsWithSingleDate = {
    assigned_to: loaderDeps.filters.assigned_to
      ? loaderDeps.filters.assigned_to.join(",")
      : undefined,
    created_at: loaderDeps.filters.created_at,
    identifier: loaderDeps.filters.identifier
      ? String(loaderDeps.filters.identifier)
      : undefined,
    items_per_page: loaderDeps.items_per_page,
    "order[assigned_to]": loaderDeps.orders?.assigned_to,
    "order[identifier]": loaderDeps.orders?.identifier,
    "order[plant]": loaderDeps.orders?.plant,
    "order[pr_item.requisition_date]":
      loaderDeps.orders?.["pr_item.requisition_date"],
    "order[proposals_count]": loaderDeps.orders?.proposals_count,
    "order[purchase_group]": loaderDeps.orders?.purchase_group,
    "order[renegotiation]": loaderDeps.orders?.renegotiation,
    "order[requester]": loaderDeps.orders?.requester,
    "order[short_description]": loaderDeps.orders?.short_description,
    "order[status]": loaderDeps.orders?.status,
    page: loaderDeps.page,
    plant_ids: loaderDeps.filters.plant_ids,
    pr_number: loaderDeps.filters.pr_number,
    proposals_count: loaderDeps.filters.proposals_count,
    purchase_group_ids: loaderDeps.filters.purchase_group_ids,
    reference_number: loaderDeps.filters.reference_number,
    renegotiation: loaderDeps.filters.renegotiation,
    requester: loaderDeps.filters.requester
      ? loaderDeps.filters.requester.join(",")
      : undefined,
    short_description: loaderDeps.filters.short_description,
    status_name: Array.isArray(loaderDeps.filters.status_name)
      ? loaderDeps.filters.status_name.join(",")
      : loaderDeps.filters.status_name,
  };

  const {
    data: serviceQuotationsResponse,
    isError,
    isLoading,
  } = useListServiceQuotations(listParams);

  useEffect(() => {
    if (isError) {
      enqueueSnackbar(t("serviceQuotation.fetchError"));
    }
  }, [isError, t]);

  const handlePageChange = useCallback(
    (_event: React.ChangeEvent<unknown>, value: number) => {
      navigate({
        search: (prev) => ({
          ...prev,
          page: value,
        }),
      });
    },
    [navigate],
  );

  const handleSort = useCallback(
    (column: keyof OrderSearchSchema) => {
      const isAsc = order[column] === "asc";
      const isDesc = order[column] === "desc";

      if (isDesc) {
        navigate({
          search: (prev) => {
            const newOrder = { ...prev.order };
            delete newOrder[column];

            return {
              ...prev,
              order: newOrder,
              page: 1,
            };
          },
        });
        return;
      }

      navigate({
        search: (prev) => ({
          ...prev,
          order: {
            [column]: isAsc ? "desc" : "asc",
          },
          page: 1,
        }),
      });
    },
    [navigate, order],
  );

  const handleTabChange = useCallback(
    (_event: React.SyntheticEvent, newValue: TabType) => {
      navigate({
        search: (prev) => ({
          ...prev,
          page: 1,
          status_name: undefined,
          tab: newValue,
        }),
      });
    },
    [navigate],
  );

  return (
    <PageContainer
      maxWidth={false}
      showBackButton={false}
      title={t("serviceQuotation.title")}
    >
      <AppCard>
        <ServiceQuotationsFilters
          loading={isLoading}
          onApply={() => {
            navigate({
              search: (prev) => ({
                ...prev,
                page: 1,
              }),
            });
          }}
          onClear={() => {
            navigate({
              search: (prev) => ({
                ...prev,
                page: 1,
              }),
            });
          }}
        />

        <TabContext value={selectedTab}>
          <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}>
            <Tabs onChange={handleTabChange} value={selectedTab}>
              <RouteTabLink
                from="/vbuyer/service_quotations/"
                label={t("quotation.tabs.all")}
                search={(prev) => ({
                  ...prev,
                  page: 1,
                  status_name: undefined,
                  tab: "all",
                })}
                to="/vbuyer/service_quotations"
                value="all"
              />
              <RouteTabLink
                from="/vbuyer/service_quotations/"
                label={t("quotation.tabs.open")}
                search={(prev) => ({
                  ...prev,
                  page: 1,
                  status_name: undefined,
                  tab: "open",
                })}
                to="/vbuyer/service_quotations"
                value="open"
              />
              <RouteTabLink
                from="/vbuyer/service_quotations/"
                label={t("quotation.tabs.ongoing")}
                search={(prev) => ({
                  ...prev,
                  page: 1,
                  status_name: undefined,
                  tab: "ongoing",
                })}
                to="/vbuyer/service_quotations"
                value="ongoing"
              />
              <RouteTabLink
                from="/vbuyer/service_quotations/"
                label={t("quotation.tabs.pendingBuyers")}
                search={(prev) => ({
                  ...prev,
                  page: 1,
                  status_name: undefined,
                  tab: "pending_buyer",
                })}
                to="/vbuyer/service_quotations"
                value="pending_buyer"
              />
              <RouteTabLink
                from="/vbuyer/service_quotations/"
                label={t("quotation.tabs.completed")}
                search={(prev) => ({
                  ...prev,
                  page: 1,
                  status_name: undefined,
                  tab: "completed",
                })}
                to="/vbuyer/service_quotations"
                value="completed"
              />
            </Tabs>
          </Box>
        </TabContext>

        <ListServiceQuotations
          loading={isLoading}
          onSort={handleSort}
          order={order}
          serviceQuotations={serviceQuotationsResponse?.results || []}
        />

        <Box sx={{ display: "flex", justifyContent: "center", marginTop: 2 }}>
          <Pagination
            color="primary"
            count={Math.ceil(
              (serviceQuotationsResponse?.total_items || 0) / rowsPerPage,
            )}
            onChange={handlePageChange}
            page={page}
          />
        </Box>
      </AppCard>
    </PageContainer>
  );
};

// biome-ignore assist/source/useSortedKeys: ordering required for type inference, waiting a TanStack Biome plugin
export const Route = createFileRoute("/_private/vbuyer/service_quotations/")({
  validateSearch: searchSchema,
  loaderDeps: ({ search }: { search: ServiceQuotationsSearch }) => {
    const selectedStatusNames = parseSearchStrings(search.status_name);
    let statusFilter: string[] | undefined;

    if (search.tab === "open") {
      statusFilter = ["Waiting Send"];
    } else if (search.tab === "ongoing") {
      statusFilter = [
        "Waiting Offers",
        "Waiting Approval",
        "Checking Proposal",
        "Checking Lines Changes",
        "Waiting Change of Details",
        "Issuing Purchase Order",
        "Choosing Winner",
        "Renegotiable",
        "Renegotiation",
      ];
    } else if (search.tab === "pending_buyer") {
      statusFilter = ["Pending Buyer"];
    } else if (["completed", "finished"].includes(search.tab)) {
      statusFilter = ["Finished", "Cancelled"];
    }

    return {
      filters: {
        assigned_to: parseSearchIds(search.assigned_to),
        created_at: search.created_at,
        identifier: search.identifier ? Number(search.identifier) : undefined,
        plant_ids: parseSearchIds(search.plant_ids),
        pr_number: search.pr_number,
        proposals_count: search.proposals_count,
        purchase_group_ids: parseSearchIds(search.purchase_group_ids),
        reference_number: search.reference_number,
        renegotiation: search.renegotiation,
        requester: parseSearchIds(search.requester),
        short_description: search.short_description,
        status_name:
          search.tab === "all"
            ? selectedStatusNames
            : selectedStatusNames?.length
              ? statusFilter?.filter((status) =>
                  selectedStatusNames.includes(status),
                )
              : statusFilter,
      },
      items_per_page: search.rowsPerPage || 25,
      orders: {
        assigned_to: search.order.assigned_to,
        identifier: search.order.identifier,
        plant: search.order.plant,
        "pr_item.requisition_date": search.order["pr_item.requisition_date"],
        proposals_count: search.order.proposals_count,
        purchase_group: search.order.purchase_group,
        renegotiation: search.order.renegotiation,
        requester: search.order.requester,
        short_description: search.order.short_description,
        status: search.order.status,
      },
      page: search.page || 1,
    };
  },
  beforeLoad({ context }) {
    if (context.auth.user?.roles_names.includes("Service Quotation - View")) {
      return;
    }

    throw new ForbiddenError();
  },
  loader: ({ context, deps }) => {
    context.queryClient.prefetchQuery(serviceQuotationsListQueryOptions(deps));

    return null;
  },
  component: ServiceQuotations,
});
